import {Chip} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getFertility} from '../../Redux/Thunk/fertiltyActions';
import '../../styles/Components/Fertility.scss';
import {formatDate} from '../../utils/dateTime';
import FertilityTable from './FertilityTable';
import Header from './PageHeader';

const FertilityView = ({loaderchange, popupalert, popuptext}) => {
  const dispatch = useDispatch();

  const [pagination, setPagination] = useState({pageIndex: 1, pageSize: 10});
  const [tableData, setTableData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [viewType, setViewType] = useState('table');

  const sideactive = useSelector(state => state.counter.sidebarnav);
  const patientDetails = useSelector(state => state.fertility.patientDetails);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const dataPayload = {
      p_uhid: patientDetails.uuid
    };

    loaderchange('true');

    try {
      const data = await dispatch(getFertility(dataPayload)).unwrap();
      const fertility = data.data;
      if (fertility.length > 0) {
        setTableData(fertility);
        setDataCount(data.total_count);
      } else {
        popupalert('true');
        setTableData([]);
        setDataCount(0);
        popuptext('No Data Available');
        setTimeout(() => popupalert('false'), 2000);
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      popupalert('true');
      popuptext('Failed to fetch data');
      setTimeout(() => popupalert('false'), 2000);
    } finally {
      loaderchange('false');
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Treatment Type',
        accessor: 'treatment_type'
      },
      {
        Header: ' Cycle Number',
        accessor: 'cycle'
      },
      {
        Header: 'Start Date',
        accessor: 'start_date',
        Cell: ({value}) => {
          const formattedDate = formatDate(value);
          return (
            <div style={{marginTop: '3px'}} data-tooltip-id='my-tooltip' data-tooltip-content={formattedDate}>
              {formattedDate}
            </div>
          );
        }
      },
      {
        Header: 'Trigger Date',
        accessor: 'trigger_date',
        Cell: ({value}) => {
          const formattedDate = formatDate(value);
          return (
            <div style={{marginTop: '3px'}} data-tooltip-id='my-tooltip' data-tooltip-content={formattedDate}>
              {formattedDate}
            </div>
          );
        }
      },
      {
        Header: 'Procedure Date',
        accessor: 'procedure_date',
        Cell: ({value}) => {
          const formattedDate = formatDate(value);
          return (
            <div style={{marginTop: '3px'}} data-tooltip-id='my-tooltip' data-tooltip-content={formattedDate}>
              {formattedDate}
            </div>
          );
        }
      },
      {
        Header: 'Beta-hCG Test Date',
        accessor: 'beta_hcg_test_date',
        Cell: ({value}) => {
          const formattedDate = formatDate(value);
          return (
            <div style={{marginTop: '3px'}} data-tooltip-id='my-tooltip' data-tooltip-content={formattedDate}>
              {formattedDate}
            </div>
          );
        }
      },
      {
        Header: 'Result',
        accessor: 'result',
        Cell: ({value}) => {
          const getStatusColor = result => {
            switch (result) {
              case 'Pending':
                return 'warning';
              case 'Positive':
                return 'success';
              case 'Negative':
                return 'error';
              default:
                return 'default';
            }
          };
          return (
            <Chip
              label={value}
              color={getStatusColor(value)}
              // variant="outlined"
              size='small'
              sx={{
                height: '20px', // Reduce overall height
                padding: '0 6px', // Adjust padding
                '& .MuiChip-label': {
                  fontSize: '0.75rem', // Reduce text size
                  padding: '0 4px' // Adjust inner padding
                }
              }}
            />
          );
        }
      },
      {
        Header: 'Remarks',
        accessor: 'remarks'
      }
    ],
    []
  );

  return (
    <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
      <Header title='Cycle History' type='view' tableData={tableData} />
      <PatientCard patient={patientDetails} />
      <div className='row mt-4'>
        <div className='col-lg-12'>
          <div className='card'>
            <div className='card-body'>
              <div className='patient'>
                <div className='table-content-patient'>
                  <ul className='nav nav-tabs mb-3'>
                    <li className='nav-item'>
                      <button className={`nav-link ${viewType === 'table' ? 'active' : ''}`} onClick={() => setViewType('table')}>
                        Table View
                      </button>
                    </li>
                    <li className='nav-item'>
                      <button className={`nav-link ${viewType === 'card' ? 'active' : ''}`} onClick={() => setViewType('card')}>
                        Card View
                      </button>
                    </li>
                    <li className='nav-item'>
                      <button className={`nav-link ${viewType === 'timeline' ? 'active' : ''}`} onClick={() => setViewType('timeline')}>
                        Timeline View
                      </button>
                    </li>
                  </ul>

                  {/* <Filters filters={filters} setFilters={setFilters} setPagination={setPagination} pageType='view' /> */}
                  {viewType === 'table' && <FertilityTable data={tableData} columns={columns} pagination={pagination} setPagination={setPagination} datacount={dataCount} />}

                  {viewType === 'card' && <CardView data={tableData} />}
                  {viewType === 'timeline' && <TimelineView data={tableData} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const labelMap = {
  treatment_type: 'Treatment Type',
  cycle: 'Cycle',
  start_date: 'Start Date',
  procedure_date: 'Procedure Date',
  trigger_date: 'Trigger Date',
  beta_hcg_test_date: 'Beta-hCG Test Date',
  result: 'Result',
  remarks: 'Remarks',
  fsh: 'FSH',
  lh: 'LH',
  e2: 'E2',
  amh: 'AMH',
  tsh_prolactin: 'TSH - Prolactin',
  progesterone: 'Progesterone',
  sperm_factors: 'Sperm Factors',
  tubal_patency: 'Tubal Patency'
};

const nestedLabelMap = {
  // IVF
  embryos_transferred: 'Embryos Transferred',
  transfer_type: 'Transfer Type',
  embryo_glue: 'Embryo Glue',
  era: 'ERA',
  cervical_shaving: 'Cervical Shaving',
  anaesthesia: 'Anaesthesia',
  embryo_grading: 'Embryo Grading',
  pgt_a_result: 'PGT-A Result',
  endometrial_thickness: 'Endometrial Thickness',

  // IUI
  follicle_status: 'Follicle Status',
  number_of_follicles: 'Number of Follicles',
  tmsc: 'TMSC',
  type_of_iui: 'Type of IUI',
  sitting: 'Sitting',

  // OI
  number_of_follicles_ruptured: 'Follicles Ruptured'
  // note: endometrial_thickness also reused
};

const formatNestedData = (data, type) => {
  if (!data) return null;

  return (
    <div className='border rounded p-2 mt-3 bg-light'>
      <h6 className='text-primary'>{type.toUpperCase()} Details</h6>
      <div className='row'>
        {Object.entries(data).map(([key, value]) => {
          if (['id', 'deleted', 'deleted_by_cascade', 'created_at', 'updated_at', 'last_updated_by'].includes(key)) return null;

          const label = nestedLabelMap[key];
          return label ? (
            // <p key={key} className='mb-1'>
            //   <strong>{label}:</strong> {value}
            // </p>
            <div className='col-md-4 mb-2' key={key}>
              <p className='mb-1'>
                <strong>{label}:</strong> {value}
              </p>
            </div>
          ) : null;
        })}
      </div>
    </div>
  );
};
const getStatusColor = result => {
  switch (result) {
    case 'Positive':
      return 'success';
    case 'Negative':
      return 'danger';
    default:
      return 'default';
  }
};

const CardView = ({data}) => {
  return (
    <div className='row'>
      {data.map((item, index) => (
        <div className='col-12 mb-3' key={index}>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <div className='row'>
                {Object.entries(item).map(([key, value]) => {
                  if (['patient_name', 'patient_age', 'patient_p_uhid', 'ivf', 'iui', 'oi'].includes(key)) return null;
                  if (value === null || value === '') return null;

                  const label = labelMap[key];
                  const formattedValue = key.includes('date') ? formatDate(value) : key === 'result' ? `<span class="badge bg-${getStatusColor(value)}">${value}</span>` : value;

                  return label ? (
                    <div className='col-md-4 mb-2' key={key}>
                      <p className='mb-1'>
                        <strong>{label}:</strong> <span dangerouslySetInnerHTML={{__html: formattedValue}} />
                      </p>
                    </div>
                  ) : null;
                })}
              </div>

              {/* Nested sections (not wrapped in 3-col layout unless needed) */}
              {item.ivf && formatNestedData(item.ivf, 'ivf')}
              {item.iui && formatNestedData(item.iui, 'iui')}
              {item.oi && formatNestedData(item.oi, 'oi')}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
const TimelineView = ({data}) => {
  return (
    <div className='container py-4'>
      <div className='timeline'>
        {data.map((item, index) => (
          <div key={index} className='timeline-item mb-4 position-relative ps-4 border-start border-2 border-primary'>
            {/* Dot marker */}
            <div className='position-absolute top-0 start-0 translate-middle bg-primary rounded-circle' style={{width: '1rem', height: '1rem'}}></div>

            {/* Timeline card */}
            <div className='card shadow-sm'>
              <div className='card-body'>
                {/* Main data */}
                {Object.entries(item).map(([key, value]) => {
                  if (['patient_name', 'patient_age', 'patient_p_uhid', 'ivf', 'iui', 'oi'].includes(key) || value === null || value === '') return null;

                  const label = labelMap[key];
                  const formattedValue = key.includes('date')
                    ? `<small class="text-muted">${formatDate(value)}</small>`
                    : key === 'result'
                    ? `<span class="badge bg-${getStatusColor(value)}">${value}</span>`
                    : value;

                  return label ? (
                    <p key={key} className='mb-1'>
                      <strong>{label}:</strong> <span dangerouslySetInnerHTML={{__html: formattedValue}} />
                    </p>
                  ) : null;
                })}

                {/* IVF / IUI / OI Sections */}
                {['ivf', 'iui', 'oi'].map(section =>
                  item[section] ? (
                    <div key={section} className='mt-3'>
                      <h6 className='text-primary text-uppercase'>{section} Details</h6>
                      <div className='ms-3 border-start ps-3 border-secondary'>{formatNestedData(item[section], section)}</div>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PatientCard = ({patient}) => (
  <div className='card shadow-sm bg-light'>
    <div className='card-body'>
      <h5 className='card-title text-primary mb-4'>
        <i className='bi bi-person-badge me-2' />
        Patient Information
      </h5>
      <p className='card-text mb-2'>
        <i className='bi bi-upc-scan me-2 text-secondary' />
        <strong>UUID:</strong> {patient.uuid}
      </p>
      <p className='card-text mb-2'>
        <i className='bi bi-person-fill me-2 text-secondary' />
        <strong>Name:</strong> {patient.name}
      </p>
      <p className='card-text'>
        <i className='bi bi-calendar2-week me-2 text-secondary' />
        <strong>Age:</strong> {patient.age}
      </p>
    </div>
  </div>
);

// <div className='card shadow-sm bg-light'>

export default FertilityView;
